import {convertISOToDate} from "@/utils";
import {UNCLASSIFIED} from "@/marketing/tableQuery";

// Libellés des valeurs gérées par ms-marketing (voir les enums du microservice)

// Intention d'un contenu, choisie par l'utilisateur. Les valeurs gardent le jargon du marketing (TOFU / MOFU / BOFU), l'écran n'affiche que les libellés.
// color : couleur de la barre de répartition (du plus clair au plus foncé, comme l'entonnoir), textColor : texte lisible dessus.
export const FUNNEL_STAGE_ENUM = {
    TOFU: {label: "Se faire connaître", value: "TOFU", key: "tofu", color: "#66cccc", textColor: "#17233f", help: "Dire qu'on existe, toucher du monde, sans rien vendre."},
    MOFU: {label: "Donner confiance", value: "MOFU", key: "mofu", color: "#4a67ad", textColor: "#ffffff", help: "Montrer son savoir-faire : cas client, retour d'expérience, expertise."},
    BOFU: {label: "Convertir", value: "BOFU", key: "bofu", color: "#24396b", textColor: "#ffffff", help: "Inciter à agir : demander un devis, s'inscrire, acheter, nous contacter."},
};

// Choix des filtres sur l'intention : les trois intentions et « À classer » (contenus importés d'un réseau, dont l'intention n'est pas encore choisie)
export const UNCLASSIFIED_LABEL = "À classer";
export const UNCLASSIFIED_HELP = "Publication importée : choisissez à quoi elle sert (ouvrez-la). Elle n'entre pas dans la répartition tant qu'elle n'est pas classée.";
export const FUNNEL_STAGE_FILTER_ENUM = {
    ...FUNNEL_STAGE_ENUM,
    UNCLASSIFIED: {label: UNCLASSIFIED_LABEL, value: UNCLASSIFIED},
};

// Rythme d'un sujet. min / max : longueur conseillée du texte, en caractères
export const CADENCE_ENUM = {
    WEEKLY: {label: "Contenu de la semaine", value: "WEEKLY", min: 1200, max: 1800},
    PILLAR: {label: "Contenu du mois", value: "PILLAR", min: undefined, max: 2200},
};

// Réseau d'une publication. maxImages : nombre maximum d'images (un post LinkedIn n'en a qu'une, un carrousel Instagram plusieurs)
export const CHANNEL_ENUM = {
    LINKEDIN_PAGE: {label: "LinkedIn", value: "LINKEDIN_PAGE", maxImages: 1},
    INSTAGRAM: {label: "Instagram", value: "INSTAGRAM", maxImages: 4},
};

// Statut d'un sujet : calculé par le service d'après ses publications, jamais saisi
export const TOPIC_STATUS_ENUM = {
    IDEA: {label: "Idée", value: "IDEA"},
    DRAFTING: {label: "En rédaction", value: "DRAFTING"},
    READY: {label: "Prêt", value: "READY"},
    SCHEDULED: {label: "Programmé", value: "SCHEDULED"},
    PUBLISHED: {label: "Publié", value: "PUBLISHED"},
    PARKED: {label: "De côté", value: "PARKED"},
};

export const PUBLICATION_STATUS_ENUM = {
    DRAFT: {label: "Brouillon", value: "DRAFT"},
    SCHEDULED: {label: "Programmée", value: "SCHEDULED"},
    PUBLISHED: {label: "Publiée", value: "PUBLISHED"},
};

export const TEXT_STATUS_ENUM = {
    NONE: {label: "Pas de texte", value: "NONE"},
    DRAFT: {label: "Brouillon", value: "DRAFT"},
    FINAL: {label: "Terminé", value: "FINAL"},
};

export const IMAGE_STATUS_ENUM = {
    NOT_REQUIRED: {label: "Pas d'image", value: "NOT_REQUIRED"},
    TO_DO: {label: "À faire", value: "TO_DO"},
    GENERATED: {label: "Générée", value: "GENERATED"},
    VALIDATED: {label: "Validée", value: "VALIDATED"},
};

// Les six résultats d'une publication publiée. Un chiffre absent est un chiffre inconnu (et non zéro).
export const METRICS = [
    {key: "impressions", label: "Impressions", help: "Nombre de fois où le post s'est affiché"},
    {key: "reactions", label: "Réactions", help: "J'aime et autres réactions"},
    {key: "comments", label: "Commentaires", help: ""},
    {key: "shares", label: "Partages", help: "Republications du post"},
    {key: "clicks", label: "Clics", help: "Clics sur le lien ou sur « voir plus »"},
    {key: "leads", label: "Demandes reçues", help: "Messages, devis ou inscriptions que vous attribuez à ce post (LinkedIn ne peut pas le savoir)"},
];

// Les documents bruts de la base portent « _id », les lignes de l'API « id »
export function idOf(item) {
    return item ? (item.id ?? item._id) : undefined;
}

export function labelOf(enumObject, value, fallback = '-') {
    const found = Object.values(enumObject).find(item => item.value === value);
    return found ? found.label : fallback;
}

export function enumToSelectValues(enumObject) {
    return Object.values(enumObject).map(item => ({label: item.label, value: item.value}));
}

export function formatDate(date) {
    return date ? new Date(date).toLocaleDateString("fr-FR") : '-';
}

export function formatDateTime(date) {
    return date ? new Date(date).toLocaleString("fr-FR", {day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit"}) : '-';
}

// Valeur d'un champ <input type="date"> (AAAA-MM-JJ), vide si la date est absente
export function toInputDate(date) {
    return date ? convertISOToDate(date) : '';
}

// Valeur d'un champ <input type="datetime-local"> (AAAA-MM-JJTHH:mm, heure locale), vide si la date est absente
export function toInputDateTime(date) {
    if (!date) return '';
    const value = new Date(date);
    if (isNaN(value.getTime())) return '';
    const pad = number => String(number).padStart(2, '0');
    return `${convertISOToDate(value)}T${pad(value.getHours())}:${pad(value.getMinutes())}`;
}

// Date saisie (date ou date et heure locale) envoyée au service en ISO ; vide si rien n'est saisi
export function fromInputDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date.toISOString();
}

export function formatNumber(value) {
    return typeof value === 'number' ? value.toLocaleString('fr-FR') : '-';
}

// Moyenne affichée avec au plus une décimale, « - » quand le chiffre n'est connu pour aucune publication
export function formatAverage(value) {
    return typeof value === 'number' ? value.toLocaleString('fr-FR', {maximumFractionDigits: 1}) : '-';
}

// Nombre de caractères d'un texte tel que LinkedIn le compte (retours à la ligne compris)
export function countCharacters(text) {
    return Array.from(String(text ?? '').replace(/\r\n/g, '\n')).length;
}

// Hashtags saisis dans un seul champ (« #facturation entrepreneur, #tva ») : un tableau, sans doublon ni « # » (le service le remet)
export function parseHashtags(text) {
    const tags = String(text ?? '').split(/[\s,;]+/).map(tag => tag.replace(/^#+/, '').trim()).filter(Boolean);
    return [...new Set(tags)];
}

export function hashtagsToText(hashtags) {
    return (hashtags ?? []).join(' ');
}

// Part de chaque intention dans l'objectif et dans les contenus réalisés + à venir : l'intention la plus en retard est proposée pour le prochain contenu.
// Ne s'appuie que sur le comptage des intentions choisies, jamais sur des statistiques de performance.
export function suggestStage(report) {
    if (!report) return undefined;
    const recent = report.published.last3Months.counts;
    const upcoming = report.upcoming.counts;
    const counts = {};
    Object.values(FUNNEL_STAGE_ENUM).forEach(stage => counts[stage.key] = (recent[stage.key] ?? 0) + (upcoming[stage.key] ?? 0));
    const total = Object.values(counts).reduce((sum, count) => sum + count, 0);

    let best;
    let bestGap = 0;
    Object.values(FUNNEL_STAGE_ENUM).forEach(stage => {
        const share = total === 0 ? 0 : counts[stage.key] * 100 / total;
        const gap = report.objective[stage.key] - share;
        if (gap > bestGap) {
            best = {stage: stage.value, share: Math.round(share), objective: report.objective[stage.key]};
            bestGap = gap;
        }
    });
    return best;
}

// Copie un texte dans le presse-papiers ; repli pour un navigateur sans l'API (page non sécurisée)
export async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (error) {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        let copied = false;
        try {
            copied = document.execCommand('copy');
        } catch (e) {
            copied = false;
        }
        document.body.removeChild(area);
        return copied;
    }
}

const RESPONSE_MARKERS = [
    {key: 'post', marker: '===POST==='},
    {key: 'firstComment', marker: '===PREMIER COMMENTAIRE==='},
    {key: 'image', marker: '===IMAGE==='},
    {key: 'checks', marker: '===À VÉRIFIER==='},
];

// Découpe la réponse de l'outil d'écriture selon les quatre repères demandés par le prompt. Renvoie undefined si la réponse n'en contient pas.
export function splitResponse(text) {
    const source = String(text ?? '').replace(/\r\n/g, '\n');
    const found = RESPONSE_MARKERS
        .map(item => ({...item, index: source.indexOf(item.marker)}))
        .filter(item => item.index >= 0)
        .sort((a, b) => a.index - b.index);
    if (!found.some(item => item.key === 'post')) return undefined;

    const parts = {};
    found.forEach((item, position) => {
        const start = item.index + item.marker.length;
        const end = position + 1 < found.length ? found[position + 1].index : source.length;
        parts[item.key] = source.substring(start, end).trim();
    });
    // Sans image, le prompt demande d'écrire « aucune »
    if (/^aucune?$/i.test(String(parts.image ?? '').replace(/[«»"'“”.\s]/g, ''))) parts.image = '';
    // Les points à vérifier sont une liste (« - point » ou « 1. point »)
    parts.checks = String(parts.checks ?? '').split('\n').map(line => line.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '').trim()).filter(Boolean);
    return parts;
}
